import { usePostHog } from 'posthog-js/react';
import { ErrorBoundary } from 'react-error-boundary';

import { resolveChunkLoadRecovery } from './chunk-load-recovery.js';
import { ErrorBoundaryRender } from './error-boundary-render.js';
import { GenericErrorBoundaryRender } from './generic-error-boundary-render.js';

import type { ReactNode } from 'react';

export type GenericErrorBoundaryProps = {
	children?: ReactNode | undefined;
};

export function GenericErrorBoundary({ children }: GenericErrorBoundaryProps) {
	const posthog = usePostHog();

	return (
		<ErrorBoundary
			onError={(error, info) => {
				// Note: This may log the same error that was already logged by QueryCache/MutationCache
				// if it originated from a React Query error. Both logs are kept for debugging:
				// - QueryCache/MutationCache logs include queryKey/mutationKey
				// - Error boundary logs include componentStack
				posthog.captureException(error, {
					extra: {
						componentStack: info.componentStack,
						source: 'error-boundary',
						url: window.location.href,
					},
					chunk_load_recovery: resolveChunkLoadRecovery(error),
				});
			}}
			fallbackRender={({ error, resetErrorBoundary }) => (
				<ErrorBoundaryRender
					error={error}
					renderUnknownError={() => (
						<GenericErrorBoundaryRender
							buttonText='Reset'
							error={error as unknown}
							onButtonClick={resetErrorBoundary}
						/>
					)}
				/>
			)}
		>
			{children}
		</ErrorBoundary>
	);
}
